import { useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldAlert } from "lucide-react";
import { api } from "../../api";
import { ErrorState } from "../../components/common/ErrorState";
import { RevenueBreakdownPanel } from "../../components/control/RevenueBreakdownPanel";
import { BillingDunningTable } from "../../components/control/BillingDunningTable";
import { TakeRateConfigPanel } from "../../components/control/TakeRateConfigPanel";
import { AdShareSettlementPanel } from "../../components/control/AdShareSettlementPanel";
import { MONETIZATION } from "../../constants/testIds";
import { useKairooData } from "../../contexts/KairooDataContext";
import { fadeUp } from "../../lib/motion";

// UX-CP-02 §1 SCR-P08-30/31/32/33 "Monetization" — requires `billing`.
export default function MonetizationDashboard() {
  const reducedMotion = useReducedMotion();
  const { me } = useKairooData();
  const hasBilling = (me?.kairoo_permissions || []).includes("billing");

  const summaryQ = useQuery({
    queryKey: ["platform-summary"],
    queryFn: async () => (await api.get("/control/platform-summary")).data,
    enabled: hasBilling,
  });
  const gymsQ = useQuery({
    queryKey: ["control-gyms"],
    queryFn: async () => (await api.get("/control/gyms")).data.gyms,
    enabled: hasBilling,
  });

  // testing-agent finding (iteration_18, CRITICAL): the sidebar hiding this nav link is
  // cosmetic only — direct URL navigation must be blocked too (defense-in-depth, same pattern
  // GymDetail already applies to its Seats/Suspend controls). The backend also now withholds
  // platform_revenue from a non-billing session, but this page must never render the panels
  // (or fire their queries) at all without `billing`.
  if (!hasBilling) {
    return (
      <div data-testid={MONETIZATION.page} className="max-w-5xl">
        <div className="solid-surface rounded-[14px] p-16 flex flex-col items-center text-center" data-testid={MONETIZATION.forbidden}>
          <ShieldAlert size={28} style={{ color: "var(--status-overdue-text)" }} className="mb-3" />
          <p className="font-semibold" style={{ color: "var(--scarlet-text)" }}>Monetization requires the 'billing' permission</p>
          <p className="text-sm mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>Ask a platform operator with billing access to grant it, or view a section you already have access to.</p>
        </div>
      </div>
    );
  }

  if (summaryQ.isError) return <ErrorState error={summaryQ.error} onRetry={() => summaryQ.refetch()} title="Couldn't load Monetization" />;

  return (
    <div data-testid={MONETIZATION.page} className="space-y-6 max-w-5xl pb-8">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-3xl font-bold" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}>Monetization</h1>
        <p className="text-base mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>Platform revenue, billing health, and take-rate/ad-share controls.</p>
      </motion.div>

      <RevenueBreakdownPanel revenue={summaryQ.data?.platform_revenue} loading={summaryQ.isLoading} />
      <BillingDunningTable gyms={gymsQ.data} loading={gymsQ.isLoading} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <TakeRateConfigPanel gyms={gymsQ.data} />
        <AdShareSettlementPanel />
      </div>
    </div>
  );
}
